import AIImageManager from './AIImageManager';

class AIImageElement extends HTMLElement {
  private img: HTMLImageElement | null = null;
  private container: HTMLDivElement | null = null;
  private menuButton: HTMLDivElement | null = null;
  private menu: HTMLDivElement | null = null;
  private modal: HTMLDivElement | null = null;
  private isLoading: boolean = false;
  private recordId: number | null = null;
  private modificationsHistory: any[] = [];

  constructor() {
    super();
  }

  static get observedAttributes() {
    return ['prompt', 'width', 'height', 'alt', 'class', 'style'];
  }

  connectedCallback() {
    if (!this.container) {
      this.setupElements();
      this.setupStyles();
    }

    this.applyAttributes();
    setTimeout(() => this.loadImage(), 0);
  }

  private setupElements() {
    // Create container
    this.container = document.createElement('div');
    this.container.className = 'ai-img-container';
    this.container.style.cssText = `
      position: relative;
      display: inline-block;
      border-radius: 8px;
      padding: 4px;
      background: linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #f7b731, #5f27cd, #ff6b6b);
      background-size: 300% 300%;
      animation: ai-img-glow 3s ease infinite;
    `;

    // Create image
    this.img = document.createElement('img');
    this.img.style.cssText = 'display: block; border-radius: 4px;';

    // Create menu button (three dots)
    this.menuButton = document.createElement('div');
    this.menuButton.className = 'ai-img-menu-btn';
    this.menuButton.innerHTML = '⋯';
    this.menuButton.style.cssText = `
      position: absolute;
      top: 4px;
      right: 4px;
      width: 24px;
      height: 24px;
      background: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(8px);
      color: white;
      border-radius: 50%;
      display: none;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 16px;
      font-weight: bold;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      z-index: 1001;
    `;

    // Create dropdown menu
    this.menu = document.createElement('div');
    this.menu.className = 'ai-img-menu';
    this.menu.style.cssText = `
      position: absolute;
      top: 32px;
      right: 4px;
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(12px);
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      display: none;
      flex-direction: column;
      min-width: 160px;
      overflow: hidden;
      z-index: 1002;
      border: 1px solid rgba(0, 0, 0, 0.06);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    `;

    const editBtn = this.createMenuItem('✏️ 修改', () => this.showEditModal());
    const regenerateBtn = this.createMenuItem('🔄 重新生成', () => this.regenerateImage());
    const historyBtn = this.createMenuItem('📜 查看历史', () => this.showHistoryModal());
    const infoBtn = this.createMenuItem('ℹ️ 查看信息', () => this.showInfoModal());

    this.menu.appendChild(editBtn);
    this.menu.appendChild(regenerateBtn);
    this.menu.appendChild(historyBtn);
    this.menu.appendChild(infoBtn);

    // Assemble elements
    this.container.appendChild(this.img);
    this.container.appendChild(this.menuButton);
    this.container.appendChild(this.menu);
    this.appendChild(this.container);

    let hoverTimer: number | null = null;
    let leaveTimer: number | null = null;

    // Event listeners with delay
    this.container.addEventListener('mouseenter', () => {
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimer = null;
      }

      hoverTimer = window.setTimeout(() => {
        if (this.menuButton) this.menuButton.style.display = 'flex';
      }, 300);
    });

    this.container.addEventListener('mouseleave', () => {
      if (hoverTimer) {
        clearTimeout(hoverTimer);
        hoverTimer = null;
      }

      leaveTimer = window.setTimeout(() => {
        if (this.menuButton) this.menuButton.style.display = 'none';
        if (this.menu) this.menu.style.display = 'none';
      }, 500);
    });

    this.menuButton.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.menu) {
        this.menu.style.display = this.menu.style.display === 'flex' ? 'none' : 'flex';
      }
    });

    // Close menu when clicking outside
    document.addEventListener('click', () => {
      if (this.menu) this.menu.style.display = 'none';
    });
  }

  private setupStyles() {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes ai-img-glow {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      .ai-img-menu-btn:hover {
        background: rgba(0, 0, 0, 0.85) !important;
        transform: scale(1.1);
      }
      .ai-img-menu-item {
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .ai-img-menu-item:hover {
        background: #f5f5f5 !important;
        transform: translateX(4px);
      }
    `;
    document.head.appendChild(style);
  }

  private updateReplaceCode() {
    // This method is no longer needed as we moved the functionality to showInfoModal
  }

  private createMenuItem(text: string, onClick: () => void): HTMLDivElement {
    const item = document.createElement('div');
    item.className = 'ai-img-menu-item';
    item.textContent = text;
    item.style.cssText = `
      padding: 12px 16px;
      cursor: pointer;
      font-size: 14px;
      white-space: nowrap;
      color: #333;
      font-weight: 500;
    `;
    item.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
      if (this.menu) this.menu.style.display = 'none';
    });
    return item;
  }

  private showEditModal() {
    // Create modal
    this.modal = document.createElement('div');
    this.modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 24px;
      max-width: 500px;
      width: 90%;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    `;

    const title = document.createElement('h3');
    title.textContent = '修改提示词';
    title.style.cssText = 'margin: 0 0 16px 0; font-size: 18px; font-weight: 600;';

    const input = document.createElement('textarea');
    input.value = this.getAttribute('prompt') || '';
    input.style.cssText = `
      width: 100%;
      min-height: 100px;
      padding: 12px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 14px;
      resize: vertical;
      box-sizing: border-box;
      font-family: inherit;
    `;

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = 'display: flex; gap: 12px; margin-top: 16px; justify-content: flex-end;';

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = '取消';
    cancelBtn.style.cssText = `
      padding: 8px 16px;
      border: 1px solid #ddd;
      background: white;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
    `;
    cancelBtn.addEventListener('click', () => this.closeModal());

    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = '确认';
    confirmBtn.style.cssText = `
      padding: 8px 16px;
      border: none;
      background: #1890ff;
      color: white;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
    `;
    confirmBtn.addEventListener('click', async () => {
      const newPrompt = input.value.trim();
      if (newPrompt && this.recordId !== null) {
        this.closeModal();
        await this.modifyImage(newPrompt);
      } else {
        this.closeModal();
      }
    });

    buttonContainer.appendChild(cancelBtn);
    buttonContainer.appendChild(confirmBtn);

    modalContent.appendChild(title);
    modalContent.appendChild(input);
    modalContent.appendChild(buttonContainer);

    this.modal.appendChild(modalContent);
    document.body.appendChild(this.modal);

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });

    input.focus();
  }

  private closeModal() {
    if (this.modal) {
      document.body.removeChild(this.modal);
      this.modal = null;
    }
  }

  private showHistoryModal() {
    // Create modal
    this.modal = document.createElement('div');
    this.modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 24px;
      max-width: 800px;
      width: 90%;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    `;

    const title = document.createElement('h3');
    title.textContent = '历史记录';
    title.style.cssText = 'margin: 0 0 16px 0; font-size: 18px; font-weight: 600;';

    const historyContainer = document.createElement('div');
    historyContainer.style.cssText = 'display: flex; flex-direction: column; gap: 16px;';

    if (this.modificationsHistory.length === 0) {
      const emptyMsg = document.createElement('p');
      emptyMsg.textContent = '暂无历史记录';
      emptyMsg.style.cssText = 'color: #999; text-align: center; padding: 20px;';
      historyContainer.appendChild(emptyMsg);
    } else {
      this.modificationsHistory.forEach((item) => {
        const historyItem = document.createElement('div');
        historyItem.style.cssText = `
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 12px;
          display: flex;
          gap: 12px;
          cursor: pointer;
          transition: background 0.2s;
        `;
        historyItem.addEventListener('mouseenter', () => {
          historyItem.style.background = '#f5f5f5';
        });
        historyItem.addEventListener('mouseleave', () => {
          historyItem.style.background = 'white';
        });

        const img = document.createElement('img');
        img.src = item.modified_image_url;
        img.style.cssText = `
          width: 120px;
          height: 120px;
          object-fit: cover;
          border-radius: 4px;
        `;

        const info = document.createElement('div');
        info.style.cssText = 'flex: 1; display: flex; flex-direction: column; gap: 4px;';

        const promptText = document.createElement('div');
        promptText.textContent = item.is_regenerate ? '🔄 重新生成' : `✏️ ${item.modification_prompt}`;
        promptText.style.cssText = 'font-size: 14px; font-weight: 500;';

        const timeText = document.createElement('div');
        timeText.textContent = item.created_at;
        timeText.style.cssText = 'font-size: 12px; color: #999;';

        const idText = document.createElement('div');
        idText.textContent = `ID: ${item.id}`;
        idText.style.cssText = 'font-size: 12px; color: #666;';

        info.appendChild(promptText);
        info.appendChild(timeText);
        info.appendChild(idText);

        historyItem.appendChild(img);
        historyItem.appendChild(info);

        // Click to use this image
        historyItem.addEventListener('click', () => {
          if (this.img) {
            this.img.src = item.modified_image_url;
          }
          this.closeModal();
        });

        historyContainer.appendChild(historyItem);
      });
    }

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '关闭';
    closeBtn.style.cssText = `
      margin-top: 16px;
      padding: 8px 16px;
      border: 1px solid #ddd;
      background: white;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      width: 100%;
    `;
    closeBtn.addEventListener('click', () => this.closeModal());

    modalContent.appendChild(title);
    modalContent.appendChild(historyContainer);
    modalContent.appendChild(closeBtn);

    this.modal.appendChild(modalContent);
    document.body.appendChild(this.modal);

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });
  }

  private showInfoModal() {
    // Create modal
    this.modal = document.createElement('div');
    this.modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 24px;
      max-width: 600px;
      width: 90%;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    `;

    const title = document.createElement('h3');
    title.textContent = '组件信息';
    title.style.cssText = 'margin: 0 0 20px 0; font-size: 18px; font-weight: 600;';

    const infoContainer = document.createElement('div');
    infoContainer.style.cssText = 'display: flex; flex-direction: column; gap: 16px;';

    // Current image preview
    const previewSection = document.createElement('div');
    previewSection.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

    const previewLabel = document.createElement('div');
    previewLabel.textContent = '当前图片';
    previewLabel.style.cssText = 'font-size: 13px; font-weight: 600; color: #666;';

    const previewImg = document.createElement('img');
    if (this.img?.src) {
      previewImg.src = this.img.src;
    }
    previewImg.style.cssText = `
      max-width: 100%;
      max-height: 200px;
      object-fit: contain;
      border-radius: 8px;
      border: 1px solid #e0e0e0;
    `;

    previewSection.appendChild(previewLabel);
    previewSection.appendChild(previewImg);

    // Component attributes
    const attrsSection = document.createElement('div');
    attrsSection.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

    const attrsLabel = document.createElement('div');
    attrsLabel.textContent = '组件属性';
    attrsLabel.style.cssText = 'font-size: 13px; font-weight: 600; color: #666;';

    const attrsContent = document.createElement('div');
    attrsContent.style.cssText = `
      background: #f5f5f5;
      padding: 12px;
      border-radius: 8px;
      font-size: 12px;
      color: #666;
      font-family: 'Monaco', 'Menlo', monospace;
      line-height: 1.6;
    `;

    const prompt = this.getAttribute('prompt') || '';
    const width = this.getAttribute('width') || '';
    const height = this.getAttribute('height') || '';
    const alt = this.getAttribute('alt') || '';

    let attrsText = '';
    if (prompt) attrsText += `prompt="${prompt}"\n`;
    if (width) attrsText += `width="${width}"\n`;
    if (height) attrsText += `height="${height}"\n`;
    if (alt) attrsText += `alt="${alt}"\n`;
    if (this.recordId) attrsText += `\nrecord_id: ${this.recordId}`;

    attrsContent.textContent = attrsText || '无属性';

    attrsSection.appendChild(attrsLabel);
    attrsSection.appendChild(attrsContent);

    // Replacement code
    const replaceSection = document.createElement('div');
    replaceSection.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

    const replaceLabel = document.createElement('div');
    replaceLabel.textContent = '替换为静态 <img> 标签';
    replaceLabel.style.cssText = 'font-size: 13px; font-weight: 600; color: #666;';

    const replaceCode = document.createElement('code');
    replaceCode.style.cssText = `
      background: #f5f5f5;
      padding: 12px;
      border-radius: 8px;
      font-size: 11px;
      font-family: 'Monaco', 'Menlo', monospace;
      color: #666;
      word-break: break-all;
      cursor: pointer;
      transition: all 0.2s;
      border: 1px solid #e0e0e0;
      display: block;
    `;

    const imgTag = this.generateImgTag();
    replaceCode.textContent = imgTag;

    const copyBtn = document.createElement('button');
    copyBtn.textContent = '📋 复制代码';
    copyBtn.style.cssText = `
      padding: 8px 16px;
      border: none;
      background: #4ecdc4;
      color: white;
      border-radius: 6px;
      cursor: pointer;
      font-size: 13px;
      font-weight: 500;
      transition: all 0.2s;
      margin-top: 8px;
    `;

    copyBtn.addEventListener('click', () => {
      navigator.clipboard.writeText(imgTag);
      copyBtn.textContent = '✓ 已复制';
      copyBtn.style.background = '#45b7d1';
      setTimeout(() => {
        copyBtn.textContent = '📋 复制代码';
        copyBtn.style.background = '#4ecdc4';
      }, 2000);
    });

    copyBtn.addEventListener('mouseenter', () => {
      copyBtn.style.background = '#45b7d1';
    });

    copyBtn.addEventListener('mouseleave', () => {
      if (copyBtn.textContent === '📋 复制代码') {
        copyBtn.style.background = '#4ecdc4';
      }
    });

    replaceCode.addEventListener('click', () => {
      copyBtn.click();
    });

    replaceSection.appendChild(replaceLabel);
    replaceSection.appendChild(replaceCode);
    replaceSection.appendChild(copyBtn);

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '关闭';
    closeBtn.style.cssText = `
      margin-top: 16px;
      padding: 8px 16px;
      border: 1px solid #ddd;
      background: white;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      width: 100%;
    `;
    closeBtn.addEventListener('click', () => this.closeModal());

    infoContainer.appendChild(previewSection);
    infoContainer.appendChild(attrsSection);
    infoContainer.appendChild(replaceSection);

    modalContent.appendChild(title);
    modalContent.appendChild(infoContainer);
    modalContent.appendChild(closeBtn);

    this.modal.appendChild(modalContent);
    document.body.appendChild(this.modal);

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });
  }

  private generateImgTag(): string {
    if (!this.img?.src) return '';

    const width = this.getAttribute('width') || '';
    const height = this.getAttribute('height') || '';
    const alt = this.getAttribute('alt') || '';
    const className = this.getAttribute('class') || '';
    const style = this.getAttribute('style') || '';

    let imgTag = '<img';
    imgTag += ` src="${this.img.src}"`;
    if (alt) imgTag += ` alt="${alt}"`;
    if (width) imgTag += ` width="${width}"`;
    if (height) imgTag += ` height="${height}"`;
    if (className) imgTag += ` class="${className}"`;
    if (style) imgTag += ` style="${style}"`;
    imgTag += ' />';

    return imgTag;
  }

  private async regenerateImage() {
    if (this.recordId === null || this.isLoading || !this.img) return;

    this.isLoading = true;
    this.showLoading();

    try {
      const manager = AIImageManager.getInstance();
      const result = await manager.regenerateImage(this.recordId);

      if (result.modified_image_url) {
        this.img.src = result.modified_image_url;
        // Keep using original_record_id for all subsequent operations
        if (result.original_record_id) {
          this.recordId = result.original_record_id;
        }
        // Update modifications history
        if (result.modifications_history) {
          this.modificationsHistory = result.modifications_history;
        } else if (result.modifications) {
          this.modificationsHistory = result.modifications;
        }
      }

      this.img.onload = () => {
        this.hideLoading();
      };

      this.img.onerror = () => {
        console.error('ai-img: Image failed to load');
        this.showError();
      };
    } catch (error) {
      console.error('ai-img: Failed to regenerate image', error);
      this.showError();
    } finally {
      this.isLoading = false;
    }
  }

  private async modifyImage(modificationPrompt: string) {
    if (this.recordId === null || this.isLoading || !this.img) return;

    this.isLoading = true;
    this.showLoading();

    try {
      const manager = AIImageManager.getInstance();
      const result = await manager.modifyImage(this.recordId, modificationPrompt);

      if (result.modified_image_url) {
        this.img.src = result.modified_image_url;
        // Keep using original_record_id for all subsequent operations
        if (result.original_record_id) {
          this.recordId = result.original_record_id;
        }
        // Update modifications history
        if (result.modifications_history) {
          this.modificationsHistory = result.modifications_history;
        } else if (result.modifications) {
          this.modificationsHistory = result.modifications;
        }
      }

      this.img.onload = () => {
        this.hideLoading();
      };

      this.img.onerror = () => {
        console.error('ai-img: Image failed to load');
        this.showError();
      };
    } catch (error) {
      console.error('ai-img: Failed to modify image', error);
      this.showError();
    } finally {
      this.isLoading = false;
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue && this.isConnected && this.img) {
      if (name === 'class' || name === 'style') {
        this.applyClassAndStyle();
      } else {
        this.applyAttributes();
      }
    }
  }

  private applyAttributes() {
    if (!this.img) return;

    const width = this.getAttribute('width');
    const height = this.getAttribute('height');
    const alt = this.getAttribute('alt') || '';

    if (width) this.img.setAttribute('width', width);
    if (height) this.img.setAttribute('height', height);
    this.img.alt = alt;

    this.applyClassAndStyle();
  }

  private applyClassAndStyle() {
    if (!this.img) return;

    const className = this.getAttribute('class');
    const style = this.getAttribute('style');

    if (className) this.img.className = className;
    if (style) this.img.setAttribute('style', style);
  }

  private async loadImage() {
    if (this.isLoading || !this.img) return;

    const prompt = this.getAttribute('prompt');
    if (!prompt) {
      console.warn('ai-img: prompt attribute is required');
      return;
    }

    this.isLoading = true;
    this.showLoading();

    try {
      const manager = AIImageManager.getInstance();
      const width = this.getAttribute('width') ? parseInt(this.getAttribute('width')!) : undefined;
      const height = this.getAttribute('height') ? parseInt(this.getAttribute('height')!) : undefined;

      const result = await manager.generateImage({
        user_input: prompt,
        ...(width && { width }),
        ...(height && { height })
      });

      // Extract image URL from result
      let imageUrl: string;

      // Store modifications history if available
      if (result.modifications) {
        this.modificationsHistory = result.modifications;
      }

      // If has modifications, use the last one (most recent)
      if (result.modifications && result.modifications.length > 0) {
        const lastModification = result.modifications[result.modifications.length - 1];
        imageUrl = lastModification.modified_image_url;
      } else if (result.final_url) {
        imageUrl = result.final_url;
      } else if (result.processed_url) {
        imageUrl = result.processed_url;
      } else if (result.original_url) {
        imageUrl = result.original_url;
      } else if (result.url) {
        imageUrl = result.url;
      } else if (result.image_url) {
        imageUrl = result.image_url;
      } else if (result.data) {
        imageUrl = `data:image/png;base64,${result.data}`;
      } else {
        throw new Error('Unknown response format');
      }

      // Store record_id if available
      if (result.record_id) {
        this.recordId = result.record_id;
      }

      this.img.src = imageUrl;

      this.img.onload = () => {
        this.hideLoading();
      };

      this.img.onerror = () => {
        console.error('ai-img: Image failed to load');
        this.showError();
      };
    } catch (error) {
      console.error('ai-img: Failed to generate image', error);
      this.showError();
    } finally {
      this.isLoading = false;
    }
  }

  private showLoading() {
    if (!this.img) return;
    this.img.style.opacity = '0.5';
    this.img.alt = 'Loading...';
  }

  private hideLoading() {
    if (!this.img) return;
    this.img.style.opacity = '1';
    this.updateReplaceCode();
  }

  private showError() {
    if (!this.img) return;
    this.hideLoading();
    this.img.alt = 'Failed to load image';
    this.img.style.opacity = '0.3';
  }
}

export default AIImageElement;
